import type { AccessSession, AuthSession, PublicUser } from "@fortress/shared";
import {
  ApiError,
  fetchMe,
  logout as logoutOnServer,
  refreshSession,
  UnauthorizedError,
} from "./api";
import {
  clearStoredTokens,
  getStoredTokens,
  setStoredTokens,
  type StoredTokens,
} from "./storage";

// Client-side session lifecycle. Authenticated calls send ONLY the bearer JWT;
// the refresh token leaves storage only when a call comes back 401 — then
// `refreshStoredSession` rotates it via `POST /auth/refresh` and the call is
// retried once. Boot and join go through `ensureValidSession`, which asks the
// server (`GET /auth/me`) whether the stored session still maps to a real
// user — local `exp` alone is not enough.

export async function saveSession(session: AuthSession): Promise<void> {
  await setStoredTokens({
    accessToken: session.accessToken,
    refreshToken: session.refreshToken,
  });
}

/** Persist a renewed access token; the stored refresh token stays as-is. */
export async function saveAccessSession(session: AccessSession): Promise<void> {
  const tokens = await getStoredTokens();
  if (!tokens) throw new Error("not_authenticated");
  await setStoredTokens({
    accessToken: session.accessToken,
    refreshToken: tokens.refreshToken,
  });
}

export async function clearSession(): Promise<void> {
  await clearStoredTokens();
}

export async function logout(): Promise<void> {
  const tokens = await getStoredTokens();
  if (tokens) await logoutOnServer(tokens.refreshToken);
  await clearSession();
}

/**
 * Rotate the stored refresh token for a fresh pair (`POST /auth/refresh`) and
 * persist it. The ONLY place the refresh token is sent outside logout.
 *
 * Single-flight: concurrent callers share one `/auth/refresh` call. The server
 * rotates strictly, so two racing refreshes (e.g. parallel polls both hitting
 * 401 at access-token expiry) would have the loser replay the just-revoked
 * token — which the server reads as theft and answers with the family revoked,
 * logging the user out.
 *
 * Returns null only when nothing is stored or the server answered 401
 * `refresh_failed` — its explicit "this refresh token is revoked/expired"
 * code, and the caller's cue to clear the session. Anything else (5xx,
 * network, unexpected status) rethrows with the stored tokens left intact,
 * so a later attempt can still succeed.
 */
export function refreshStoredSession(): Promise<StoredTokens | null> {
  refreshInFlight ??= doRefreshStoredSession().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

let refreshInFlight: Promise<StoredTokens | null> | null = null;

async function doRefreshStoredSession(): Promise<StoredTokens | null> {
  const tokens = await getStoredTokens();
  if (!tokens) return null;
  try {
    const session = await refreshSession(tokens.refreshToken);
    await saveSession(session);
    return {
      accessToken: session.accessToken,
      refreshToken: session.refreshToken,
    };
  } catch (err) {
    if (
      err instanceof ApiError &&
      err.status === 401 &&
      err.message === "refresh_failed"
    ) {
      return null;
    }
    throw err;
  }
}

/**
 * Run an authenticated call with the stored access token. If the server
 * rejects the JWT (the wrapped call throws `UnauthorizedError`), refresh once
 * and retry. Throws `Error("not_authenticated")` — after clearing the stored
 * session — only when there is no session or the refresh token was
 * definitively rejected; a transient refresh failure (5xx, network) rethrows
 * with the session left intact. Caller redirects to `/auth` on
 * `not_authenticated`.
 */
export async function withAuthRetry<T>(
  run: (accessToken: string) => Promise<T>,
): Promise<T> {
  const tokens = await getStoredTokens();
  if (!tokens) throw new Error("not_authenticated");
  try {
    return await run(tokens.accessToken);
  } catch (err) {
    if (!(err instanceof UnauthorizedError)) throw err;
    // Throws on transient refresh failures — propagates, tokens kept.
    const fresh = await refreshStoredSession();
    if (!fresh) {
      await clearSession();
      throw new Error("not_authenticated");
    }
    return run(fresh.accessToken);
  }
}

export interface ValidSession {
  tokens: StoredTokens;
  user: PublicUser;
}

/**
 * Return a usable access token + user only if the server still recognizes the
 * session and the user exists. Calls `GET /auth/me` with the JWT alone; on a
 * 401 it refreshes (the one time the refresh token is sent) and retries once.
 * Returns null when the session can't be validated — but the stored tokens are
 * cleared ONLY on a definitive rejection (refresh token refused, account
 * gone); transient failures (5xx, network) keep them for a later attempt.
 * Caller redirects to `/auth` on null.
 */
export async function ensureValidSession(): Promise<ValidSession | null> {
  let tokens = await getStoredTokens();
  if (!tokens) return null;

  let user: PublicUser | null;
  try {
    user = await fetchMe(tokens.accessToken);
  } catch (err) {
    if (!(err instanceof UnauthorizedError)) return null; // transient — keep tokens
    try {
      tokens = await refreshStoredSession();
    } catch {
      return null; // transient refresh failure — keep tokens
    }
    if (!tokens) {
      // The server definitively rejected the refresh token — session is dead.
      await clearSession();
      return null;
    }
    try {
      user = await fetchMe(tokens.accessToken);
    } catch {
      // A freshly-minted JWT failing is a server problem, not a dead session.
      return null;
    }
  }
  if (!user) {
    await clearSession(); // definitive: the account no longer exists (404)
    return null;
  }
  return { tokens, user };
}

/**
 * Return a usable access token only if the server still recognizes the session
 * and the user exists. Prefer `ensureValidSession` when the profile is needed.
 */
export async function ensureValidAccessToken(): Promise<StoredTokens | null> {
  const session = await ensureValidSession();
  return session?.tokens ?? null;
}

/** Whether a server-validated session exists. */
export async function hasSession(): Promise<boolean> {
  return (await ensureValidAccessToken()) !== null;
}
